/**
 * Booking several treatments in one reservation.
 *
 * A spa is the one place where "which package" is the wrong question. A couple
 * arriving at Marajah books two Relaxing and one Tonic in the same visit, and
 * the provider needs all three lines on one booking — not three separate
 * reservations with three references, which is what the bucket would have made
 * of it (owner, 2026-09-14).
 *
 * Everything else still books exactly one package. The gate is the category, so
 * a venue moving into Spas picks the behaviour up without a code change.
 */

export const MULTI_PACK_CATEGORY_SLUGS: readonly string[] = ["spas"];

/** Does this activity let a guest pick more than one package? */
export function takesMultiplePacks(categorySlug: string | null | undefined): boolean {
  if (!categorySlug) return false;
  return MULTI_PACK_CATEGORY_SLUGS.includes(categorySlug.trim().toLowerCase());
}

/**
 * Caps. A spa visit is a handful of treatments; anything past this is a group
 * booking our team arranges by hand, and the numbers exist so a crafted
 * request cannot ask for ten thousand massages.
 */
export const MAX_PACK_LINES = 10;
export const MAX_PACK_QUANTITY = 20;

export interface PackLine {
  packageId: string;
  quantity: number;
}

/**
 * Fold a client's selection into what the server will store: duplicates of the
 * same package merged into one line, quantities clamped, empty lines dropped.
 *
 * Merging rather than rejecting duplicates is deliberate — a UI that lets you
 * add the same treatment twice should end up with quantity 2, not an error.
 */
export function normalizePackLines(lines: readonly PackLine[] | null | undefined): PackLine[] {
  if (!lines || lines.length === 0) return [];
  const merged = new Map<string, number>();
  for (const line of lines) {
    const id = line?.packageId?.trim();
    if (!id) continue;
    const qty = Math.floor(Number(line.quantity));
    if (!Number.isFinite(qty) || qty <= 0) continue;
    merged.set(id, Math.min(MAX_PACK_QUANTITY, (merged.get(id) ?? 0) + qty));
  }
  return Array.from(merged, ([packageId, quantity]) => ({ packageId, quantity })).slice(
    0,
    MAX_PACK_LINES,
  );
}

/** Heads in the booking: a spa line is priced per person, so quantity is people. */
export function packLinesGuestCount(lines: readonly PackLine[]): number {
  return lines.reduce((sum, line) => sum + line.quantity, 0);
}

/**
 * How many people one of this treatment covers.
 *
 * Most treatments are one person each. A duo ritual ("Voyage Complice, for
 * two", Mawli) is sold as one treatment for a fixed party: per group
 * (`typePrice 3`) with `maxGuests` set. It is priced once per treatment, but
 * two people come, so the spa plans two places and the guest is asked who
 * both of them are. A 1400 MAD duo booked as "1 person in total" sent the spa
 * one guest for a couple (website test, 2026-11-01).
 */
export function packPeopleCovered(
  pkg: { typePrice?: number | null; maxGuests?: number | null } | null | undefined,
): number {
  if (!pkg || pkg.typePrice !== 3) return 1;
  const n = Math.floor(Number(pkg.maxGuests ?? 0));
  return Number.isFinite(n) && n > 1 ? n : 1;
}

/**
 * People in a multi-treatment booking: each line's quantity times the people
 * one of it covers. `peopleOf` answers for a package id (1 when unknown).
 */
export function packLinesPeopleCount(
  lines: readonly PackLine[],
  peopleOf: (packageId: string) => number,
): number {
  return lines.reduce((sum, line) => sum + line.quantity * Math.max(1, peopleOf(line.packageId) || 1), 0);
}
